/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   msalConfig.ts
 *     Description     :   MSAL PublicClientApplication instance configured from Vite environment variables.
 *     Module          :   005-web-shell
 *     Component Type  :   Configuration
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   @azure/msal-browser
 *     Internal        :   (none)
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     msalInstance
 *
 * =========================================================================================
 */

import { PublicClientApplication, type Configuration } from "@azure/msal-browser";

const msalConfiguration: Configuration = {
  auth: {
    clientId: import.meta.env["VITE_ENTRA_CLIENT_ID"] as string,
    authority: `https://login.microsoftonline.com/${import.meta.env["VITE_ENTRA_TENANT_ID"] as string}`,
    redirectUri: (import.meta.env["VITE_ENTRA_REDIRECT_URI"] as string | undefined) ?? window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    // After processing the redirect at redirectUri ("/"), stay there instead of navigating back to
    // the page sign-in was initiated from (/signin). Otherwise an authenticated user is bounced to
    // the login screen (it renders regardless of auth) — the classic MSAL sign-in loop. The router's
    // "/" -> LandingRedirect then forwards them to their start page.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // localStorage persists the PKCE state across the OAuth redirect; sessionStorage can be cleared
    // mid-flight by some browsers, causing handleRedirectPromise() to return null and producing
    // the sign-in loop (the user signs in at Microsoft but comes back as unauthenticated).
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfiguration);
